import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { registerClient, getClientsByRut } from '../api';
import useCurrentUser from '../hooks/useCurrentUser';
import { hasPermission } from '../utils/permissions';

const ClientRegistrationPage = () => {
  const navigate = useNavigate();
  const [client, setClient] = useState({
    name: '',
    rut: '',
    email: '',
    address: '',
    city: '',
    phone: '',
    giro: '',
    creditLimit: '',
    creditBlocked: false,
    creditWarningNote: ''
  });
  const [error, setError] = useState('');
  const { user } = useCurrentUser();
  const canCreateClient = hasPermission(user, 'client.create');
  const canReadClient = hasPermission(user, 'client.read');
  const canManageCredit = hasPermission(user, 'branch.access.any');

  const formatRut = (e) => {
    setClient({ ...client, rut: e.target.value.replace(/[^0-9kK]/g, '').replace(/^(\d{1,2})(\d{3})(\d{3})([0-9kK])$/, '$1.$2.$3-$4').toUpperCase() });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setClient({ ...client, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canCreateClient) return;
    setError('');
    try {
      if (canReadClient) {
        const clients = await getClientsByRut(client.rut);
        const existingClient = clients.pop();
        if (existingClient) {
          navigate(`/client`);
          return;
        }
      }
      const payload = { ...client };
      if (canManageCredit) {
        const trimmedLimit = String(client.creditLimit ?? '').trim();
        if (trimmedLimit === '') {
          payload.creditLimit = null;
        } else {
          const parsedLimit = Number(trimmedLimit);
          if (!Number.isFinite(parsedLimit) || parsedLimit < 0) {
            setError('El limite de credito debe ser un numero valido.');
            return;
          }
          payload.creditLimit = parsedLimit;
        }
        payload.creditBlocked = Boolean(client.creditBlocked);
        payload.creditWarningNote = client.creditWarningNote?.trim() || null;
      } else {
        delete payload.creditLimit;
        delete payload.creditBlocked;
        delete payload.creditWarningNote;
      }
      await registerClient(payload);
      navigate('/client');
    } catch (error) {
      console.error('Error creating client:', error);
      setError('No se pudo registrar el cliente.');
    }
  };

  return (
    <div className="max-w-lg mx-auto p-6 bg-white">
      <h1 className="text-2xl font-bold mb-4">Registrar Cliente</h1>
      {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
      <form onSubmit={handleSubmit} className="space-y-4">
        <input name="rut" placeholder="RUT" value={client.rut} onChange={formatRut} required className="p-2 border rounded w-full" disabled={!canCreateClient} />
          <>
            <input name="name" placeholder="Nombre" value={client.name} onChange={handleChange} required className="p-2 border rounded w-full" disabled={!canCreateClient} />
            <input name="email" type="email" placeholder="Email" value={client.email} onChange={handleChange} required className="p-2 border rounded w-full" disabled={!canCreateClient} />
            <input name="giro" placeholder="Giro" value={client.giro} onChange={handleChange} className="p-2 border rounded w-full" disabled={!canCreateClient} />
            <input name="address" placeholder="Dirección" value={client.address} onChange={handleChange} className="p-2 border rounded w-full" disabled={!canCreateClient} />
            <input name="city" placeholder="Ciudad" value={client.city} onChange={handleChange} className="p-2 border rounded w-full" disabled={!canCreateClient} />
            <input name="phone" placeholder="Teléfono" value={client.phone} onChange={handleChange} className="p-2 border rounded w-full" disabled={!canCreateClient} />
          </>
          {canManageCredit && (
            <div className="border-t pt-4 space-y-3">
              <h2 className="text-sm font-semibold text-gray-700">Credito</h2>
              <input
                type="number"
                min="0"
                name="creditLimit"
                placeholder="Limite de credito (opcional)"
                value={client.creditLimit}
                onChange={handleChange}
                className="p-2 border rounded w-full"
                disabled={!canCreateClient}
              />
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  name="creditBlocked"
                  checked={client.creditBlocked}
                  onChange={handleChange}
                  disabled={!canCreateClient}
                />
                Bloquear credito para este cliente
              </label>
              <textarea
                name="creditWarningNote"
                placeholder="Nota de advertencia"
                value={client.creditWarningNote}
                onChange={handleChange}
                className="p-2 border rounded w-full"
                rows={3}
                disabled={!canCreateClient}
              />
            </div>
          )}
        <div className="flex justify-between mt-4">
          <button type="button" className="px-4 py-2 bg-gray-300 rounded" onClick={() => navigate('/client')}>Cancelar</button>
            {canCreateClient && (
              <button type="submit" className="px-4 py-2 bg-blue-500 text-white rounded">Registrar</button>
            )}
        </div>
      </form>
    </div>
  );
};

export default ClientRegistrationPage;
